/**
 * Push-notification opt-in prompt for the Weather Almanac. Users who enable
 * this get FCM Web Push notifications when severe weather fires in their
 * locality.
 */
import { Bell, BellOff, Loader2, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { usePushNotifications } from "@/lib/pushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

export default function PushOptIn() {
  const { user } = useAuth();
  const { supported, permission, enable, sendTest, configured } = usePushNotifications();
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(permission === "granted");

  if (!configured || !supported) return null;

  if (!user) {
    return (
      <div
        className="mt-6 rounded-xl border p-4 flex items-center justify-between gap-3"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="push-optin-signed-out"
      >
        <div className="flex items-center gap-3">
          <Bell className="w-4 h-4" />
          <div>
            <div className="text-sm font-medium">Get severe weather alerts</div>
            <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              Sign in to receive push notifications for tornadoes, flooding, and other severe events near you.
            </div>
          </div>
        </div>
        <Link to="/login" className="wofy-btn-primary text-sm" data-testid="push-signin-cta">Sign in</Link>
      </div>
    );
  }

  const onEnable = async () => {
    setBusy(true);
    try {
      await enable();
      setEnabled(true);
      toast.success("Weather alerts enabled for this device.");
    } catch (e) {
      toast.error(e.message || "Couldn't enable notifications.");
    } finally { setBusy(false); }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      await sendTest();
      toast.success("Test notification sent. Check your notification tray.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn't send test.");
    } finally { setBusy(false); }
  };

  if (permission === "denied") {
    return (
      <div
        className="mt-6 rounded-xl border p-4 flex items-center gap-3"
        style={{ borderColor: "#fca5a5", background: "#fef2f2", color: "#7f1d1d" }}
        data-testid="push-optin-denied"
      >
        <BellOff className="w-4 h-4" />
        <div className="text-sm">
          Notifications are blocked in your browser. To receive severe weather alerts, allow notifications
          for this site in your browser settings and reload.
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-6 rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      data-testid="push-optin"
    >
      <div className="flex items-center gap-3 min-w-0">
        {enabled ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        ) : (
          <Bell className="w-5 h-5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
        )}
        <div>
          <div className="text-sm font-medium">
            {enabled ? "Weather alerts are on" : "Get severe weather alerts"}
          </div>
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {enabled
              ? "You'll be notified when tornadoes, floods, or other severe events fire in your locality."
              : "One tap to enable push notifications for severe weather within 50 km of your saved locality."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!enabled ? (
          <button
            data-testid="push-enable-btn"
            onClick={onEnable}
            disabled={busy}
            className="wofy-btn-primary text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Enable alerts
          </button>
        ) : (
          <button
            data-testid="push-test-btn"
            onClick={onTest}
            disabled={busy}
            className="wofy-btn-ghost text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Send test
          </button>
        )}
      </div>
    </div>
  );
}
